import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import RestHttpClient, { renderValidationErrors } from '../../../../../common/RestHttpClient';
import {
  TakeoverDispatchDate,
  TakeoverPosition,
  TakeoverPositions,
} from '../../../../../generated/ApiClient';
import { EnhancedDialog } from '../../../../../component/EnhancedDialog';
import { FormDatePicker } from '../../../../../component/form/FormDatePicker';
import { usePermissions } from '../../../../../common/auth/PermissionsContext';
import { FleetHandOverRow } from '../../fleet/FleetHandOverRow';
import { handOverPositionsSchema, validPrintStatus } from '../../fleet/fleetHandOverSchema';
import {
  isTakeoverRunning,
  TakeoverFinalAction,
  TakeoverProgressPanel,
  TakeoverStatus,
} from './TakeoverProgressPanel';
import { isForbidden } from './useTakeoverFlow';

const schema = z.object({
  expectedDispatchDate: z.date(),
});

type TakeoverDispatchDateFormValues = z.infer<typeof schema>;

const fleetSchema = handOverPositionsSchema.merge(schema);

type FleetHandOverFormValues = z.infer<typeof fleetSchema>;

export const toUtcMidnight = (date: Date) =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

interface Props {
  dealId: number;
  calculationId?: number;
  isFleet?: boolean;
  status: TakeoverStatus | null;
  onClose: () => void;
  onStart: (finalAction: TakeoverFinalAction) => void;
  onShowSignDocuments: () => void;
}

export function TakeoverDispatchDateDialog({
  dealId,
  calculationId,
  isFleet = false,
  status,
  onClose,
  onStart,
  onShowSignDocuments,
}: Props) {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const canPrintDocuments =
    hasPermission('LeasingProducts', 'Print') ||
    hasPermission('FinancingProducts', 'Print') ||
    hasPermission('Services', 'Print');
  const canSign = hasPermission('SignatureDocument', 'Execute') && canPrintDocuments;
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [positionsLoaded, setPositionsLoaded] = useState(false);
  const [dialogError, setDialogError] = useState<string>();
  const isOpen = calculationId !== undefined;
  const startedRef = useRef(false);

  const dateForm = useForm<TakeoverDispatchDateFormValues>({
    resolver: zodResolver(schema),
  });
  const positionsForm = useForm<FleetHandOverFormValues>({
    resolver: zodResolver(fleetSchema),
    defaultValues: { positions: [] },
  });
  const positions = useWatch({ control: positionsForm.control, name: 'positions' }) ?? [];

  const running = isTakeoverRunning(status);
  const flowStarted = status !== null;
  const locked = loading || flowStarted;
  const hasSelection = !isFleet || positions.some((p) => p.selected);
  const documentDone = status?.document === 'done';
  const signDocumentsAvailable = status?.finalAction === 'sign' && documentDone;

  const handleCancel = () => {
    if (!loading && !running) onClose();
  };

  const loadDispatchDate = async (calcId: number) => {
    const { data } = await RestHttpClient.getTakeoverDispatchDate(dealId, calcId);
    const stored = data.expectedDispatchDate;
    dateForm.reset({ expectedDispatchDate: stored ? toUtcMidnight(stored) : undefined });
  };

  const loadPositions = async () => {
    const { data } = await RestHttpClient.getFleetHandOver(dealId);
    setPositionsLoaded(true);
    const loadedPositions = data.map((x) => ({
      position: x.position,
      vin: x.vin ?? '',
      cmsId: x.cmsId ?? '',
      status: x.status,
      selected: !!x.vin && validPrintStatus.includes(x.status),
    }));
    const preselectedDates = data
      .filter((_, index) => loadedPositions[index].selected)
      .map((x) => x.expectedDispatchDate?.getTime());
    const sharedDate = preselectedDates.every((date) => date === preselectedDates[0])
      ? preselectedDates[0]
      : undefined;
    positionsForm.reset({
      positions: loadedPositions,
      expectedDispatchDate:
        sharedDate !== undefined ? toUtcMidnight(new Date(sharedDate)) : undefined,
    });
  };

  useEffect(() => {
    startedRef.current = false;
    dateForm.reset({ expectedDispatchDate: undefined });
    positionsForm.reset({ positions: [], expectedDispatchDate: undefined });
    setPositionsLoaded(false);
    setDialogError(undefined);
    setLoadFailed(false);
    if (calculationId === undefined) return;

    setLoading(true);
    (isFleet ? loadPositions() : loadDispatchDate(calculationId))
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, calculationId, isFleet]);

  const saveAndStart = (finalAction: TakeoverFinalAction) => () => {
    if (calculationId === undefined || startedRef.current) return;
    startedRef.current = true;

    const allowRestart = () => {
      startedRef.current = false;
    };
    setDialogError(undefined);

    const startWhenSaved = async (
      save: () => Promise<{ data: boolean }>,
      showRejection: (rejection: unknown) => void
    ) => {
      setLoading(true);
      try {
        const result = await save();
        if (result.data) {
          onStart(finalAction);
        } else {
          allowRestart();
          showRejection(result);
        }
      } catch (err) {
        allowRestart();
        if (isForbidden(err)) setDialogError(t('errors.calculation.takeover.forbidden'));
        else showRejection(err);
      } finally {
        setLoading(false);
      }
    };

    if (isFleet) {
      positionsForm.handleSubmit(
        (values) =>
          startWhenSaved(
            () =>
              RestHttpClient.saveTakeoverPositions(
                dealId,
                calculationId,
                new TakeoverPositions({
                  positions: values.positions.map(
                    (p) =>
                      new TakeoverPosition({
                        position: p.position,
                        vin: p.vin ?? undefined,
                        selected: p.selected,
                      })
                  ),
                  expectedDispatchDate: toUtcMidnight(values.expectedDispatchDate),
                })
              ),
            (rejection) => renderValidationErrors(rejection, positionsForm.setError)
          ),
        allowRestart
      )();
      return;
    }

    dateForm.handleSubmit(
      (values) =>
        startWhenSaved(
          () =>
            RestHttpClient.setTakeoverDispatchDate(
              dealId,
              calculationId,
              new TakeoverDispatchDate({
                expectedDispatchDate: toUtcMidnight(values.expectedDispatchDate),
              })
            ),
          (rejection) => renderValidationErrors(rejection, dateForm.setError)
        ),
      allowRestart
    )();
  };

  const refreshPositionStatuses = async () => {
    const { data } = await RestHttpClient.getFleetHandOver(dealId);
    const statusByPosition = new Map(data.map((x) => [x.position, x.status]));
    positionsForm.getValues('positions').forEach((position, index) => {
      const refreshedStatus = statusByPosition.get(position.position);
      if (refreshedStatus !== undefined) {
        positionsForm.setValue(`positions.${index}.status`, refreshedStatus);
      }
    });
  };

  useEffect(() => {
    if (!isFleet || !documentDone || !positionsLoaded) return;
    // Display only: the positions are already handed over on the server.
    refreshPositionStatuses().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFleet, documentDone, positionsLoaded]);

  const noPositions = isFleet && positionsLoaded && positions.length === 0;

  return (
    <EnhancedDialog
      maxWidth={isFleet ? 'lg' : 'md'}
      fullWidth
      isDialogOpened={isOpen}
      setIsDialogOpened={handleCancel}
      handleCancel={handleCancel}
    >
      <DialogTitle>
        {isFleet ? t('deals.takeoverDispatchDate.fleetTitle') : t('deals.takeoverDispatchDate.title')}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={4}>
          <Grid item xs={12} md={isFleet ? 8 : 5}>
            {loadFailed && (
              <Alert severity="error">
                {isFleet
                  ? t('deals.takeoverDispatchDate.positionsLoadFailed')
                  : t('deals.takeoverDispatchDate.loadFailed')}
              </Alert>
            )}
            {noPositions && (
              <Alert severity="info">{t('deals.takeoverDispatchDate.noPositions')}</Alert>
            )}
            {isFleet ? (
              <Stack gap={2} sx={{ paddingY: 3 }}>
                <Box sx={{ maxWidth: 300 }}>
                  <FormDatePicker
                    control={positionsForm.control}
                    name="expectedDispatchDate"
                    label={t('deals.takeoverDispatchDate.expectedDispatchDate')}
                    disabled={locked || loadFailed}
                    required
                  />
                </Box>
                {positions.map((field, index) => (
                  <FleetHandOverRow
                    key={field.position}
                    control={positionsForm.control}
                    index={index}
                    field={field}
                    disabled={locked}
                  />
                ))}
              </Stack>
            ) : (
              <Box sx={{ paddingY: 3 }}>
                <FormDatePicker
                  control={dateForm.control}
                  name="expectedDispatchDate"
                  label={t('deals.takeoverDispatchDate.expectedDispatchDate')}
                  disabled={locked || loadFailed}
                  required
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={isFleet ? 4 : 7}>
            <TakeoverProgressPanel status={status} />
          </Grid>
        </Grid>
        {dialogError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {dialogError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Box sx={{ textAlign: 'right' }}>
          {flowStarted ? (
            <>
              {signDocumentsAvailable && (
                <Button onClick={onShowSignDocuments} sx={{ marginRight: '4px' }}>
                  {t('deals.takeoverDispatchDate.showSignDocuments')}
                </Button>
              )}
              <Button onClick={handleCancel} disabled={running} variant="contained">
                {t('deals.takeoverStatus.close')}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleCancel}
                disabled={loading}
                sx={{ border: 1, borderColor: 'black', color: 'black', marginRight: '4px' }}
              >
                {t('back')}
              </Button>
              <Button
                onClick={saveAndStart('print')}
                variant="contained"
                disabled={locked || loadFailed || !hasSelection}
                sx={{ marginRight: '4px' }}
              >
                {t('deals.takeoverDispatchDate.print')}
              </Button>
              {canSign && (
                <Button
                  onClick={saveAndStart('sign')}
                  variant="contained"
                  disabled={locked || loadFailed || !hasSelection}
                >
                  {t('deals.takeoverDispatchDate.sign')}
                </Button>
              )}
            </>
          )}
        </Box>
      </DialogActions>
    </EnhancedDialog>
  );
}
